/**
 * Proactive token refresh on user activity (throttled).
 * Keeps the Okta access token fresh during long sessions and long-running
 * operations (e.g. generate requirements, plan, protocol) without flooding Okta.
 *
 * Throttle: 5 minutes minimum between refresh attempts.
 * Can be increased to 10 minutes (600_000 ms) if refresh traffic is a concern.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { oktaAuth } from '../services/oktaAuth';

const REFRESH_THROTTLE_MS = 300_000; // 5 minutes

const ACTIVITY_EVENTS = ['mousemove', 'click', 'keydown', 'scroll'] as const;

export function useTokenRefreshOnActivity(): void {
  const { authState } = useOktaAuth();
  const lastRefreshAt = useRef<number>(0);
  const isAuthenticated = authState?.isAuthenticated ?? false;

  const tryRefresh = useCallback(() => {
    if (!isAuthenticated) return;
    const now = Date.now();
    if (now - lastRefreshAt.current < REFRESH_THROTTLE_MS) return;

    lastRefreshAt.current = now;
    oktaAuth.tokenManager
      .renew('accessToken')
      .then(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useTokenRefreshOnActivity] Token refreshed on user activity');
        }
      })
      .catch((err) => {
        // Log but don't surface; auth state may update on next navigation or reload
        console.warn('[useTokenRefreshOnActivity] Token refresh failed:', err?.message ?? err);
      });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handlers = ACTIVITY_EVENTS.map((event) => {
      const handler = () => tryRefresh();
      return { event, handler };
    });

    handlers.forEach(({ event, handler }) => {
      window.document.addEventListener(event, handler, { passive: true });
    });

    return () => {
      handlers.forEach(({ event, handler }) => {
        window.document.removeEventListener(event, handler);
      });
    };
  }, [isAuthenticated, tryRefresh]);
}
